import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, CircleCheck } from 'lucide-react'
import { getLocations, getProducts, getSetupStatus, getVendors } from '../../api/master'
import { getOpeningBalances } from '../../api/accounting'
import { getCompany, getConfig, saveConfig } from '../../api/settings'
import { useSession } from '../../session'
import { deviceZone, zoneLabel } from '../../utils/timeZones'
import TimeZoneSelect from '../TimeZoneSelect'
import Spinner from '../Spinner'
import { StatusBadge } from '../PageParts'
import { CHANGED_EVENT, STEPS, finishOnboarding, markStepsDone, nextUnfinished, readProgress, skipOnboardingStep, startOnboardingStep } from '../../utils/onboarding'

// Worth doing too, but not counted as steps.
const EXTRAS = [
  { to: '/settings/company', label: 'Company details and logo' },
  { to: '/settings/payments', label: 'Card payments' },
  { to: '/master/users', label: 'Staff who sign in' },
]
const WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven']
const listOf = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`)

// Getting started: the set-up steps, each opening the page where it's done.
// Saving there moves on to the next step; any step can be skipped for now.
export default function OnboardingWizard({ companyId }) {
  const navigate = useNavigate()
  const [progress, setProgress] = useState(() => readProgress(companyId))
  const [ready, setReady] = useState(false)
  const [companyName, setCompanyName] = useState('')
  const { refreshSettings } = useSession()
  // The company's clock: { value, source: 'saved' | 'currency' | 'none' }.
  const [zone, setZone] = useState({ value: '', source: '' })
  const [zoneDraft, setZoneDraft] = useState('')
  const [zoneEditing, setZoneEditing] = useState(false)
  const [zoneSaving, setZoneSaving] = useState(false)
  const [zoneError, setZoneError] = useState('')

  useEffect(() => { getCompany().then((c) => setCompanyName(c?.company_name || '')).catch(() => {}) }, [])

  // Follow changes made on the step pages (a save or a skip).
  useEffect(() => {
    const reload = () => setProgress(readProgress(companyId))
    reload()
    window.addEventListener(CHANGED_EVENT, reload)
    return () => window.removeEventListener(CHANGED_EVENT, reload)
  }, [companyId])

  // Steps whose data exists already count as done, however it got there.
  useEffect(() => {
    let active = true
    ;(async () => {
      const [setup, locations, products, vendors, opening, config] = await Promise.allSettled([
        getSetupStatus(), getLocations(), getProducts({ limit: 1 }), getVendors(), getOpeningBalances(), getConfig(),
      ])
      const got = (r) => (r.status === 'fulfilled' ? r.value : null)
      const some = (r) => Array.isArray(got(r)) && got(r).length > 0
      const s = got(setup)
      const found = []
      // A zone chosen (at sign-up, here or in Settings), or one its currency gives.
      const cfg = got(config)
      if (cfg?.time_zone_source === 'saved' || cfg?.time_zone_source === 'currency') found.push('timezone')
      if (s?.has_accounts && (s?.has_control_mappings || s?.has_minimal_retail)) found.push('utilities')
      if ((got(opening)?.totals?.count || 0) > 0) found.push('opening')
      if (some(locations)) found.push('locations')
      if (some(products)) found.push('products')
      if (some(vendors)) found.push('vendors')
      if (!active) return
      if (cfg) {
        setZone({ value: cfg.time_zone || '', source: cfg.time_zone_source || '' })
        setZoneDraft(cfg.time_zone_source === 'none' ? (deviceZone() || '') : (cfg.time_zone || ''))
      }
      if (found.length) setProgress(markStepsDone(companyId, found))
      setReady(true)
    })()
    return () => { active = false }
  }, [companyId])

  const done = new Set(progress.completedStepIds)
  const skipped = new Set(progress.skippedStepIds)
  const next = nextUnfinished(progress)
  const doneCount = STEPS.filter((s) => done.has(s.id)).length
  const skippedSteps = STEPS.filter((s) => skipped.has(s.id) && !done.has(s.id))
  const finished = ready && !next

  const start = (step) => {
    // Only a step still to do becomes the one under way, so saving there moves on.
    if (!done.has(step.id)) startOnboardingStep(companyId, step.id)
    navigate(done.has(step.id) || !step.add ? step.route : step.add)
  }
  const skip = (step) => {
    const r = skipOnboardingStep(step.id, companyId)
    if (r.progress) setProgress(r.progress)
  }
  const finish = () => { finishOnboarding(companyId); navigate('/dashboard') }
  const saveZone = async () => {
    if (!zoneDraft) { setZoneError('Choose your time zone.'); return }
    setZoneSaving(true); setZoneError('')
    try {
      await saveConfig({ time_zone: zoneDraft })
      setZone({ value: zoneDraft, source: 'saved' })
      setZoneEditing(false)
      setProgress(markStepsDone(companyId, ['timezone']))
      refreshSettings?.()
    } catch (e) {
      setZoneError(e?.response?.data?.error || e.message || 'Couldn’t save the time zone.')
    } finally {
      setZoneSaving(false)
    }
  }

  return (
    <div className="ci-page">
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Getting started</h1>
            <div className="ci-title-meta">
              Set up {companyName || 'your company'} in {WORDS[STEPS.length - 1] || STEPS.length} steps. Do them in order, or skip any for now and come back to it here later.
            </div>
          </div>
        </div>
        {ready && (
          <div className="gs-progress" aria-label={`${doneCount} of ${STEPS.length} steps done`}>
            <span>{doneCount} of {STEPS.length} done{skippedSteps.length ? ` · ${skippedSteps.length} skipped` : ''}</span>
            <span className="gs-progress-bar" aria-hidden="true"><span style={{ width: `${(doneCount / STEPS.length) * 100}%` }} /></span>
          </div>
        )}
      </div>

      {!ready ? <Spinner label="Checking what’s already set up…" /> : (
        <>
          {finished && (
            <div className="gs-done">
              <CircleCheck size={18} aria-hidden="true" />
              <span>
                {companyName || 'Your company'} is set up.
                {skippedSteps.length > 0 && ` You skipped ${listOf(skippedSteps.map((s) => s.title))}; do ${skippedSteps.length === 1 ? 'it' : 'them'} here when you’re ready.`}
              </span>
              <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" onClick={finish}>Go to Overview</button>
            </div>
          )}

          <ol className="gs-steps">
            {STEPS.map((step, index) => {
              const isDone = done.has(step.id)
              const isSkipped = !isDone && skipped.has(step.id)
              const isNext = next?.id === step.id
              const state = isDone ? 'Done' : isNext ? 'Next' : isSkipped ? 'Skipped' : 'To do'
              return (
                <li key={step.id} className={`gs-step${isDone ? ' is-done' : ''}${isNext ? ' is-next' : ''}${isSkipped ? ' is-skipped' : ''}`}>
                  <span className="gs-step-mark" aria-hidden="true">{isDone ? <Check size={14} /> : index + 1}</span>
                  <div className="gs-step-text">
                    <strong>{step.title}</strong>
                    <span>{step.what}</span>
                    {step.note && !isDone && <span className="gs-step-note">{step.note}</span>}
                    {step.id === 'timezone' && isDone && !zoneEditing && zone.value && (
                      <span className="gs-step-note">{zoneLabel(zone.value)}{zone.source === 'currency' ? ', from your currency' : ''}</span>
                    )}
                    {step.id === 'timezone' && zoneError && <span className="acc-field-error" role="alert">{zoneError}</span>}
                  </div>
                  <StatusBadge label={state} tone={isDone ? 'good' : isNext ? 'info' : 'muted'} />
                  {step.inline ? (
                    <div className="gs-step-actions gs-zone-pick">
                      {isDone && !zoneEditing ? (
                        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => { setZoneDraft(zone.value || deviceZone()); setZoneEditing(true) }}>Change</button>
                      ) : (
                        <>
                          <TimeZoneSelect value={zoneDraft} onChange={(z) => { setZoneDraft(z); setZoneError('') }} aria-label="Time zone" className="ci-select" />
                          <button type="button" className={`ci-btn ci-btn-sm ${isNext || zoneEditing ? 'ci-btn-primary' : 'ci-btn-ghost'}`} disabled={zoneSaving} onClick={saveZone}>{zoneSaving ? 'Saving…' : 'Save'}</button>
                          {zoneEditing && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => { setZoneEditing(false); setZoneError('') }}>Cancel</button>}
                          {!isDone && !isSkipped && !zoneEditing && (
                            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => skip(step)} aria-label={`Skip ${step.title} for now`}>Skip</button>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                  <div className="gs-step-actions">
                    <button type="button" className={`ci-btn ci-btn-sm ${isNext ? 'ci-btn-primary' : 'ci-btn-ghost'}`} onClick={() => start(step)}>
                      {isDone ? 'Open' : isSkipped ? 'Do it now' : step.action}
                    </button>
                    {!isDone && !isSkipped && (
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => skip(step)} aria-label={`Skip ${step.title} for now`}>Skip</button>
                    )}
                  </div>
                  )}
                </li>
              )
            })}
          </ol>

          <p className="gs-extras">
            <span>Also worth doing:</span>
            {EXTRAS.map((x) => <Link key={x.to} to={x.to}>{x.label}</Link>)}
          </p>
        </>
      )}
    </div>
  )
}
